/* btg-carousel.js — By The Gaze
   Draggable + scroll-linked horizontal product carousel, transform-based for smooth
   sub-pixel motion, with a "Drag" cursor and staggered card entrance.
   Original code, no external dependencies. Honours prefers-reduced-motion. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(pointer: fine)').matches;

  var SCROLL_FACTOR = 1.2; // page-scroll pixels -> carousel drift
  var SMOOTH = 0.14; // lower = smoother / more glide

  function pageX(e) {
    return e.touches ? e.touches[0].pageX : e.pageX;
  }
  function clamp(value, min, max) {
    return value < min ? min : value > max ? max : value;
  }

  function initCarousel(viewport) {
    if (viewport.dataset.btgCarouselReady === 'true') return;
    viewport.dataset.btgCarouselReady = 'true';

    var track = viewport.querySelector('[data-btg-carousel-track]');
    if (!track) return;

    function maxScroll() {
      return Math.max(0, track.scrollWidth - viewport.clientWidth);
    }

    /* ---- shared eased scroller (drag + page-scroll feed `target`) ---- */
    var target = 0;
    var current = 0;
    var rafId = null;

    function frame() {
      current += (target - current) * SMOOTH;
      if (Math.abs(target - current) < 0.3) current = target;
      track.style.transform = 'translate3d(' + (-current).toFixed(2) + 'px,0,0)';
      rafId = current !== target ? requestAnimationFrame(frame) : null;
    }
    function play() {
      if (rafId === null) rafId = requestAnimationFrame(frame);
    }

    /* ---- drag (follows the mouse 1:1, then glides on release) ---- */
    var isDown = false;
    var lastX = 0;
    var moved = false;
    var velocity = 0;
    var momentumRaf = null;

    function stopMomentum() {
      if (momentumRaf !== null) {
        cancelAnimationFrame(momentumRaf);
        momentumRaf = null;
      }
    }
    function momentum() {
      if (isDown || Math.abs(velocity) < 0.4) {
        momentumRaf = null;
        return;
      }
      target = clamp(target + velocity, 0, maxScroll());
      velocity *= 0.92; // friction — a little glide, eases to a stop
      play();
      momentumRaf = requestAnimationFrame(momentum);
    }

    function down(e) {
      isDown = true;
      moved = false;
      velocity = 0;
      stopMomentum();
      viewport.classList.add('is-dragging');
      lastX = pageX(e);
    }
    function move(e) {
      if (!isDown) return;
      var x = pageX(e);
      var delta = x - lastX;
      lastX = x;
      if (Math.abs(delta) > 2) moved = true;
      velocity = -delta; // remember drag speed for release momentum
      target = clamp(target - delta, 0, maxScroll());
      current = target; // track the mouse exactly while dragging
      play();
    }
    function up() {
      if (!isDown) return;
      isDown = false;
      viewport.classList.remove('is-dragging');
      if (momentumRaf === null) momentumRaf = requestAnimationFrame(momentum);
    }

    viewport.addEventListener('mousedown', down);
    window.addEventListener('mousemove', move, { passive: true });
    window.addEventListener('mouseup', up);
    viewport.addEventListener('touchstart', down, { passive: true });
    viewport.addEventListener('touchmove', move, { passive: true });
    viewport.addEventListener('touchend', up);

    Array.prototype.slice.call(track.querySelectorAll('a')).forEach(function (a) {
      a.addEventListener('click', function (e) {
        if (moved) e.preventDefault(); // a drag shouldn't trigger navigation
      });
    });

    /* ---- scroll-linked drift: row scrolls left as the page scrolls ---- */
    if (!reduceMotion) {
      var lastY = window.scrollY || window.pageYOffset;
      var onPageScroll = function () {
        var y = window.scrollY || window.pageYOffset;
        var dy = y - lastY;
        lastY = y;
        if (isDown) return;
        var rect = viewport.getBoundingClientRect();
        if (rect.top > window.innerHeight || rect.bottom < 0) return; // only while in view
        target = clamp(target + dy * SCROLL_FACTOR, 0, maxScroll());
        play();
      };
      window.addEventListener('scroll', onPageScroll, { passive: true });
      window.addEventListener('resize', function () {
        target = clamp(target, 0, maxScroll());
        play();
      });
    }

    /* ---- staggered card entrance ---- */
    if (!reduceMotion) {
      var cards = Array.prototype.slice.call(track.querySelectorAll('[data-card]'));
      var section = viewport.closest('.btg-featured');
      if (cards.length && section) {
        section.classList.add('is-anim');
        cards.forEach(function (c, i) {
          c.style.transitionDelay = i * 0.06 + 's';
        });
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                track.classList.add('is-in');
                io.disconnect();
              }
            });
          },
          { threshold: 0.1 },
        );
        io.observe(viewport);
      }
    }

    /* ---- custom "Drag" cursor (desktop only) ---- */
    if (!reduceMotion && finePointer) {
      viewport.classList.add('has-custom-cursor');
      var cursor = document.createElement('div');
      cursor.className = 'btg-cursor';
      cursor.textContent = viewport.getAttribute('data-cursor-label') || 'Drag';
      document.body.appendChild(cursor);

      var cx = 0;
      var cy = 0;
      var tx = 0;
      var ty = 0;
      var cs = 0;
      var shown = false;
      var cursorRaf = null;

      function cursorLoop() {
        cx += (tx - cx) * 0.2;
        cy += (ty - cy) * 0.2;
        cs += ((shown ? 1 : 0) - cs) * 0.15;
        cursor.style.transform =
          'translate3d(' +
          cx.toFixed(1) +
          'px,' +
          cy.toFixed(1) +
          'px,0) translate(-50%,-50%) scale(' +
          cs.toFixed(3) +
          ')';
        if (!shown && cs < 0.001) {
          cursorRaf = null;
          return;
        }
        cursorRaf = requestAnimationFrame(cursorLoop);
      }
      function cursorStart() {
        if (cursorRaf === null) cursorRaf = requestAnimationFrame(cursorLoop);
      }

      viewport.addEventListener('mouseenter', function () {
        shown = true;
        cursorStart();
      });
      viewport.addEventListener('mouseleave', function () {
        shown = false;
        cursorStart();
      });
      viewport.addEventListener('mousemove', function (e) {
        tx = e.clientX;
        ty = e.clientY;
      });
    }
  }

  function init() {
    Array.prototype.slice.call(document.querySelectorAll('[data-btg-carousel]')).forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
